/**
 * Shared cart state for the storefront.
 *
 * Before this module every script that cared about the cart issued its own
 * GET /cart.js, and every mutation fired without any guard against a second
 * one already being in flight. A single homepage load produced six to nine
 * identical reads, a drawer quantity click produced a write plus a read with
 * no debounce, and a failed write produced nothing at all: no retry policy,
 * no user-visible error, just a silent no-op that looks exactly like a slow
 * response. This module is the one place the cart is read and written.
 *
 * What it gives callers:
 *
 *   APACart.get()            coalesced read. Concurrent callers share one
 *                            request; a read younger than FRESH_MS is reused.
 *   APACart.mutate(path,body) serialized write. Writes queue behind each other
 *                            so two quantity clicks cannot race, and the cached
 *                            snapshot is invalidated on completion.
 *   APACart.subscribe(fn)     pub/sub. Called with every fresh cart snapshot.
 *
 * Transport rules, applied to both reads and writes:
 *   - 429 is honored, not hammered. Retry-After wins when the server sends it;
 *     otherwise exponential backoff with full jitter. Retries are hard-capped.
 *   - 4xx other than 429 is never retried. Those are the server telling us the
 *     request itself is wrong (sold out, bad line key), and repeating it just
 *     burns budget.
 *   - Every terminal failure raises a `cart:error` event carrying a message
 *     fit to show a customer. Failing loudly is the point: a silent failure is
 *     what makes a shopper click "add" a second time.
 */
(function (window, document) {
  'use strict';

  if (window.APACart) return;

  var FRESH_MS = 800; // a read this recent is reused rather than refetched
  var MAX_ATTEMPTS = 3; // initial try plus two retries
  var BASE_BACKOFF_MS = 500;
  var MAX_BACKOFF_MS = 8000;
  var MAX_RETRY_AFTER_MS = 10000; // beyond this, give up rather than stall the UI

  var subscribers = [];
  var snapshot = null;
  var snapshotAt = 0;
  var inFlightRead = null;
  var inFlightEpoch = null;
  var writeChain = Promise.resolve();
  // Bumped by every write. A read carries the epoch it started in, so a forced
  // read can tell whether an in-flight read predates the write it is confirming.
  var epoch = 0;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  /**
   * Full jitter: a retry storm is what turns one throttled shopper into a
   * throttled cohort, and undithered backoff just re-synchronises every tab
   * onto the same instant.
   */
  function backoffDelay(attempt) {
    var ceiling = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * Math.pow(2, attempt - 1));
    return Math.floor(Math.random() * ceiling);
  }

  /**
   * RFC 9110 allows Retry-After in two forms: delta-seconds, or an HTTP date.
   * Shopify sends seconds today, but parsing only that form means a date-valued
   * header reads as "no header" and we retry early, which is the one thing this
   * function exists to prevent.
   */
  function retryAfterDelay(response) {
    var header = response.headers && response.headers.get('Retry-After');
    if (!header) return null;

    var seconds = parseFloat(header);
    if (isFinite(seconds) && seconds >= 0) return seconds * 1000;

    var timestamp = Date.parse(header);
    if (!isNaN(timestamp)) return Math.max(0, timestamp - Date.now());

    return null;
  }

  function CartError(message, status) {
    var error = new Error(message);
    error.name = 'CartError';
    error.status = status;
    return error;
  }

  /**
   * One request, with the whole retry policy in a single place so no call site
   * has to reinvent it (and no catch block can quietly refetch on its own).
   */
  function request(path, options) {
    var attempt = 0;

    function attemptOnce() {
      attempt += 1;
      return fetch(path, options).then(function (response) {
        if (response.ok) {
          return response.json();
        }

        if (response.status === 429) {
          if (attempt >= MAX_ATTEMPTS) {
            throw CartError(
              'We are having trouble reaching your cart. Please wait a moment and try again.',
              429
            );
          }
          var stated = retryAfterDelay(response);
          if (stated !== null && stated > MAX_RETRY_AFTER_MS) {
            throw CartError(
              'Your cart is busy right now. Please wait a moment and try again.',
              429
            );
          }
          var wait = stated !== null ? stated : backoffDelay(attempt);
          return sleep(wait).then(attemptOnce);
        }

        // 4xx that is not 429 means the request is wrong, not early. Shopify
        // puts the shopper-facing reason in `description` (sold out, quantity
        // unavailable), so prefer it over anything we could invent.
        if (response.status >= 400 && response.status < 500) {
          return response
            .json()
            .catch(function () {
              return null;
            })
            .then(function (body) {
              throw CartError(
                (body && (body.description || body.message)) ||
                  'That item could not be updated. Please refresh and try again.',
                response.status
              );
            });
        }

        if (attempt >= MAX_ATTEMPTS) {
          throw CartError(
            'We could not update your cart. Please try again.',
            response.status
          );
        }
        return sleep(backoffDelay(attempt)).then(attemptOnce);
      });
    }

    return attemptOnce();
  }

  function publish(cart) {
    snapshot = cart;
    snapshotAt = Date.now();

    subscribers.forEach(function (fn) {
      try {
        fn(cart);
      } catch (e) {
        // A broken subscriber must not stop the others from updating.
        if (window.console) console.error('[cart-state] subscriber failed', e);
      }
    });

    // Existing theme and app code listens for these two. Keep firing them so
    // this module can be adopted call site by call site.
    document.dispatchEvent(new CustomEvent('cart.update', { detail: cart }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent('cart:state', { detail: cart }));
    return cart;
  }

  /**
   * Raise once and only once. A failure can pass through two layers (a write's
   * confirmation read fails, then the write's own catch sees it), and firing
   * cart:error twice resets the toast and doubles any listener's recovery work.
   */
  function raise(error) {
    if (error.raised) return error;
    error.raised = true;
    document.dispatchEvent(
      new CustomEvent('cart:error', {
        detail: { message: error.message, status: error.status }
      })
    );
    return error;
  }

  function startRead() {
    var startedAt = epoch;

    inFlightRead = request(root() + 'cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (cart) {
        inFlightRead = null;
        inFlightEpoch = null;
        return publish(cart);
      })
      .catch(function (error) {
        inFlightRead = null;
        inFlightEpoch = null;
        throw raise(error);
      });

    inFlightEpoch = startedAt;
    return inFlightRead;
  }

  /**
   * Coalesced read. Callers that happen to ask at the same moment share one
   * network request instead of racing six of them at the origin.
   *
   * `force` means "I need the cart as it is after the thing I just did", so it
   * cannot be satisfied by a read that was already in flight before that thing
   * happened: that response left the server too early to contain the change.
   * Reads are stamped with the epoch they started in, and every write bumps the
   * epoch, so a forced read waits out an older read and then issues its own.
   */
  function get(options) {
    var opts = options || {};

    if (!opts.force && snapshot && Date.now() - snapshotAt < FRESH_MS) {
      return Promise.resolve(snapshot);
    }

    if (inFlightRead) {
      if (!opts.force || inFlightEpoch === epoch) return inFlightRead;
      // Older than the write we are confirming. Let it land, then read again.
      return inFlightRead.then(startRead, startRead);
    }

    return startRead();
  }

  /**
   * Serialized write. Queuing rather than firing in parallel is what stops two
   * fast quantity clicks from landing out of order and leaving the drawer
   * showing a total the server does not agree with.
   */
  function mutate(path, body) {
    var run = function () {
      // Invalidate before the request goes out, not after it returns: from this
      // moment the snapshot is known to be about to be wrong, and a read that
      // lands in between must not be trusted to confirm the write.
      invalidate();

      return request(root() + path.replace(/^\//, ''), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      })
        .then(function (result) {
          // Deliberately no re-read here. The caller redraws from a forced read
          // straight after, and doing it here as well would make every write
          // cost two reads. Invalidating is what matters: it guarantees that
          // whoever reads next talks to the server.
          invalidate();
          return result;
        })
        .catch(function (error) {
          invalidate();
          throw raise(error);
        });
    };

    // Chain onto the previous write whether it succeeded or not, so one
    // failure does not wedge the queue for the rest of the session.
    var queued = writeChain.then(run, run);
    writeChain = queued.catch(function () {});
    return queued;
  }

  function subscribe(fn) {
    if (typeof fn !== 'function') return function () {};
    subscribers.push(fn);
    if (snapshot) fn(snapshot);
    return function unsubscribe() {
      var i = subscribers.indexOf(fn);
      if (i !== -1) subscribers.splice(i, 1);
    };
  }

  /**
   * Drop the cached snapshot and open a new epoch, so the next read goes to the
   * server and any read already in flight is recognised as predating this point.
   *
   * Legacy call sites that still write the cart with their own $.ajax should
   * call this before redrawing, or redraw through a forced read.
   */
  function invalidate() {
    snapshot = null;
    snapshotAt = 0;
    epoch += 1;
  }

  window.APACart = {
    get: get,
    mutate: mutate,
    subscribe: subscribe,
    /** Last known snapshot, or null. Synchronous; never triggers a request. */
    peek: function () {
      return snapshot;
    },
    invalidate: invalidate
  };
})(window, document);
